import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../config/axiosConfig';
import config from '../../config/api';
import { setRealtimeEvent } from "../../realtime/realtimeSlice";
import {
  addItemToList,
  getRealtimeEntityFromPayload,
  isModuleMatch,
  removeItemFromList,
  updateItemInList,
} from "../../realtime/realtimeUtils";

const BASE_URL = config.backendUrl

export const fetchWorkEntries = createAsyncThunk(
  'workentries/fetchWorkEntries',
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/workentries/get-entries`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchWorkEntriesProjectTracking = createAsyncThunk(
  'workentries/fetchWorkEntriesProjectTracking',
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/workentries/getall-project-track`);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAllWorkEntries = createAsyncThunk(
  'workentries/updateAllWorkEntries',
  async (payload, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/workentries/updateall`, payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveWorkEntries = createAsyncThunk(
  'workentries/saveWorkEntries',
  async (payload, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/workentries/save-entries`, payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteTask = createAsyncThunk(
  "descriptionTasks/delete",
  async (ids, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/workentries/de-entries`, {
        data: { ids }, // ✅ send array of IDs in body
      });
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  entries: [],
  projectTrackingWorkEntries: [],
  loading: false,
  error: null,
  updateSuccess: false,
  saveSuccess: false,
};

const workentriesSlice = createSlice({
  name: 'workentries',
  initialState,
  reducers: {
    resetStatus: (state) => {
      state.updateSuccess = false;
      state.saveSuccess = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = action.payload;
      })
      .addCase(fetchWorkEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

        .addCase(fetchWorkEntriesProjectTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkEntriesProjectTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.projectTrackingWorkEntries = action.payload;
      })
      .addCase(fetchWorkEntriesProjectTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateAllWorkEntries.pending, (state) => {
        state.loading = true;
        state.updateSuccess = false;
      })
      .addCase(updateAllWorkEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.updateSuccess = true;
      })
      .addCase(updateAllWorkEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.updateSuccess = false;
      })

      .addCase(saveWorkEntries.pending, (state) => {
        state.loading = true;
        state.saveSuccess = false;
      })
      .addCase(saveWorkEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.saveSuccess = true;
      })
      .addCase(saveWorkEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.saveSuccess = false;
      })
      
      .addCase(deleteTask.pending, (state) => {
        state.loading = true;
        state.saveSuccess = false;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.loading = false;
        state.saveSuccess = true;
        const ids = Array.isArray(action.payload)
          ? action.payload.filter(Boolean)
          : [];
        if (ids.length > 0) {
          const idSet = new Set(ids.map(String));
          state.entries = (state.entries || []).filter(
            (e) => !idSet.has(String(e?._id))
          );
          state.projectTrackingWorkEntries = (
            state.projectTrackingWorkEntries || []
          ).filter((e) => !idSet.has(String(e?._id)));
        }
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.saveSuccess = false;
      })
      .addCase(setRealtimeEvent, (state, action) => {
        const { module, action: eventAction, data } = action.payload || {};
        if (!isModuleMatch(module, ["workentries", "autosavework"])) return;

        const incomingItem = getRealtimeEntityFromPayload(data);
        if (!incomingItem) return;

        const applyListUpdate = (listKey) => {
          if (eventAction === "updated") {
            state[listKey] = updateItemInList(state[listKey], incomingItem);
          }
          if (eventAction === "created") {
            state[listKey] = addItemToList(state[listKey], incomingItem);
          }
          if (eventAction === "deleted") {
            state[listKey] = removeItemFromList(state[listKey], incomingItem);
          }
        };

        applyListUpdate("entries");
        applyListUpdate("projectTrackingWorkEntries");
      });
  },
});

// Exports
export const { resetStatus } = workentriesSlice.actions;
export default workentriesSlice.reducer;
